import React, { useEffect, useMemo, useRef, useState } from 'react';
import find from "lodash/find";
import get from "lodash/get";
import isNil from "lodash/isNil";
import some from "lodash/some";
import moment from 'moment';
import { getFormattedTime } from '../../../search-module/components/date-time/mobile/helpers/helpers';
import { TimeData } from '../../../search-module/components/date-time/mobile/data/time-data';

export const useDateTimePicker = (props) => {
  const intl = props.intl;
  const isMounted = useRef(false);
  const {
    dateValue,
    setDateValue,
    timeValue,
    setTimeValue,
    index,
    spaceId,
    loadAvailability
  } = props;

  const [isFocused, setIsFocused] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [monthAvailabilityLoading, setMonthAvailabilityLoading] = useState(false);
  const [monthAvailability, setMonthAvailability] = useState([]);

  const [timeAvailabilityLoading, setTimeAvailabilityLoading] = useState(false);
  const [startTimeAvailability, setStartTimeAvailability] = useState([]);
  const [endTimeAvailability, setEndTimeAvailability] = useState([]);

  const dateTimeDisplay = useMemo(() => {
    if (!dateValue) {
      return '';
    }
    const startTime = timeValue?.startTime;
    const endTime = timeValue?.endTime;
    const dateDisplay = intl.formatDate(dateValue, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
    let timeDisplay = '';

    if (!isNil((startTime))) {
      timeDisplay += ` ${getFormattedTime(find(TimeData, (time) => time.time_index === startTime), intl)}`;
    }
    if (!isNil(startTime) && !isNil(endTime)) {
      timeDisplay += ` - ${getFormattedTime(find(TimeData, (time) => time.time_index === endTime), intl)}`;
    }
    return <div>
      <div>{dateDisplay}</div>
      <div>{timeDisplay}</div>
    </div>;
  }, [dateValue, timeValue]);

  const handleContainerFocus = () => {
    setIsFocused(true);
    setShowDropdown(true);
  };

  const blur = () => {
    setIsFocused(false);
  };

  useEffect(() => {
    isMounted.current = true;
  }, []);

  useEffect(() => {
    return blur;
  }, []);

  const handleDateChange = (date) => {
    setDateValue(date, index);
    getDayAvailability('start', true, date);
  };

  const handleTimeChange = (whichSelector, value) => {
    getDayAvailability(whichSelector, true);
    setTimeValue(value, index);
  };

  const reset = (e) => {
    e.preventDefault();
    const { handleReset } = props;
    handleReset(index);
  };

  const getMonthAvailability = (date) => {
    const month = moment(date).get('month') + 1;
    const year = moment(date).get('year');

    const query = `bookings/availability/space/${spaceId}/month?month=${month}&year=${year}`;

    setMonthAvailabilityLoading(true);

    loadAvailability(query, (response) => {
      if (!response.error && 400 > response.statusCode) {
        if (!monthAvailabilityLoading) {
          const monthAvailability = get(response, 'month_availability', []);
          setMonthAvailability(monthAvailability);
        }
        setMonthAvailabilityLoading(false);
      }
    });
  };

  const getDayAvailability = (selector, checkValidStartTime, newDate) => {
    const startTime = get(timeValue, 'startTime');

    const date = newDate || dateValue;

    if ('end' === selector && (!date || !startTime)) return null;

    const momentDate = moment(date);
    let month;
    let year;
    let day;
    if (date) {
      month = momentDate.get('month') + 1;
      year = momentDate.get('year');
      day = momentDate.get('date');
    }

    const startTimeParam = !isNil(startTime) ? `&time_index=${startTime}` : '';

    const query = `bookings/availability/space/${spaceId}/day/${selector}?year=${year}&month=${month}&day=${day}${startTimeParam}`;

    setTimeAvailabilityLoading(true);

    loadAvailability(query, (response) => {
      if (!response.error && 400 > response.statusCode) {
        if ('start' === selector) {
          const startTimes = get(response, ['start_availability'], []);
          setStartTimeAvailability(startTimes);
          if (checkValidStartTime) {
            const isStartTimesUnavailable = some(startTimes, { time_index: parseInt(startTime, 10), available: false });
            if (isStartTimesUnavailable) {
              setTimeValue({
                startTime: null,
                endTime: null
              }, index);
              setEndTimeAvailability([]);
            }
          }
          setTimeAvailabilityLoading(false);
          return;
        }
        const endTimes = get(response, ['end_availability'], []);
        setEndTimeAvailability(endTimes);
      }
      setTimeAvailabilityLoading(false);
    });
  };

  return {
    ...props,
    dateTimeDisplay,
    handleContainerFocus,
    handleReset: reset,
    isFocused,
    setIsFocused,
    showDropdown,
    setShowDropdown,
    isMounted,
    blur,
    setTimeValue: handleTimeChange,
    setDateValue: handleDateChange,
    getMonthAvailability,
    monthAvailability,
    monthAvailabilityLoading,
    getDayAvailability,
    timeAvailabilityLoading,
    startTimeAvailability,
    endTimeAvailability
  };
};
